import { type AppState, Auth0Provider } from '@auth0/auth0-react';

import type { ReactNode } from 'react';
import { AuthProvider as OidcProvider } from 'react-oidc-context';

import { AUTH, IS_OIDC_BUILD } from './config';
import { getUserManager } from './useOidcSession';

interface AuthProviderProps {
  /**
   * Called once the IdP redirects back with a code; `returnTo` is whatever
   * `loginWithRedirect({ appState: { returnTo } })` asked to restore.
   */
  onRedirectCallback: (returnTo?: string) => void;
  children: ReactNode;
}

/**
 * Mounts the SDK provider selected by `VITE_AUTH_PROVIDER`. Callers guard on
 * `AUTH_CONFIGURED` first; rendering this without config is a programming
 * error for `oidc` and a broken `Auth0Provider` for `auth0`.
 */
export function AuthProvider({ onRedirectCallback, children }: AuthProviderProps) {
  if (IS_OIDC_BUILD) {
    return (
      <OidcProvider
        userManager={getUserManager()}
        onSigninCallback={(user) => {
          const state = user?.state as { returnTo?: string } | undefined;
          onRedirectCallback(state?.returnTo);
        }}
      >
        {children}
      </OidcProvider>
    );
  }

  return (
    <Auth0Provider
      domain={AUTH.auth0Domain ?? ''}
      clientId={AUTH.clientId ?? ''}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH.audience,
        ...(AUTH.organization ? { organization: AUTH.organization } : {}),
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={(appState?: AppState) => onRedirectCallback(appState?.returnTo)}
    >
      {children}
    </Auth0Provider>
  );
}
